import { Close, DescriptionOutlined } from '@mui/icons-material';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import type { DiffChange, Template } from 'types';
import { computeDiff, formatRelativeTime } from 'views/retainerAgreement/services/diffService';

interface VersionCompareModalProps {
    template: Template | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function VersionCompareModal({ template, open, onOpenChange }: VersionCompareModalProps) {
    const [version1Id, setVersion1Id] = useState<string>('');
    const [version2Id, setVersion2Id] = useState<string>('');
    const [viewMode, setViewMode] = useState<'unified' | 'split'>('split');

    useEffect(() => {
        if (open && template) {
            const sortedVersions = [...template.versions].sort(
                (a, b) => b.versionNumber - a.versionNumber
            );
            if (sortedVersions.length >= 2) {
                setVersion2Id(sortedVersions[0].id);
                setVersion1Id(sortedVersions[1].id);
            } else if (sortedVersions.length === 1) {
                setVersion1Id(sortedVersions[0].id);
                setVersion2Id(sortedVersions[0].id);
            }
        } else if (!open) {
            setVersion1Id('');
            setVersion2Id('');
        }
    }, [open, template]);

    const versions = template
        ? [...template.versions].sort((a, b) => b.versionNumber - a.versionNumber)
        : [];
    const version1 = versions.find((version) => version.id === version1Id);
    const version2 = versions.find((version) => version.id === version2Id);

    const diff = useMemo(() => {
        return version1 && version2 ? computeDiff(version1.content, version2.content) : [];
    }, [version1, version2]);

    const stats = useMemo(() => {
        if (!diff.length) return { additions: 0, deletions: 0, unchanged: 0 };
        return diff.reduce(
            (acc, change) => {
                if (change.type === 'added') acc.additions++;
                else if (change.type === 'removed') acc.deletions++;
                else acc.unchanged++;
                return acc;
            },
            { additions: 0, deletions: 0, unchanged: 0 }
        );
    }, [diff]);

    if (!template) return null;

    const versionLabel = (versionId: string) => {
        const version = versions.find((item) => item.id === versionId);
        if (!version) return 'Select version';
        return `v${version.versionNumber} - ${formatRelativeTime(version.timestamp)} by ${version.editorName}`;
    };

    const formatLine = (value: string) =>
        value.split('\n').map((line, index) => (
            <Box key={index} sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {line || '\u00A0'}
            </Box>
        ));

    const lineNumberSx = {
        width: 50,
        px: 1.5,
        py: 0.5,
        color: 'text.secondary',
        bgcolor: 'grey.100',
        textAlign: 'right',
        flexShrink: 0,
        borderRight: 1,
        borderColor: 'divider',
        userSelect: 'none',
    } as const;

    const renderUnifiedView = () => (
        <Box
            sx={{
                height: 500,
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'grey.50',
            }}
        >
            <Box sx={{ fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 }}>
                {diff.map((change: DiffChange, index: number) => (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            bgcolor:
                                change.type === 'added'
                                    ? '#e6ffec'
                                    : change.type === 'removed'
                                      ? '#ffebe9'
                                      : 'transparent',
                            borderLeft: 3,
                            borderColor:
                                change.type === 'added'
                                    ? 'success.main'
                                    : change.type === 'removed'
                                      ? 'error.main'
                                      : 'transparent',
                        }}
                    >
                        <Box component="span" sx={lineNumberSx}>
                            {index + 1}
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                width: 30,
                                px: 1,
                                py: 0.5,
                                textAlign: 'center',
                                fontWeight: 700,
                                flexShrink: 0,
                                color:
                                    change.type === 'added'
                                        ? 'success.main'
                                        : change.type === 'removed'
                                          ? 'error.main'
                                          : 'text.disabled',
                            }}
                        >
                            {change.type === 'added' ? '+' : change.type === 'removed' ? '-' : ''}
                        </Box>
                        <Box sx={{ flex: 1, px: 1.5, py: 0.5, overflow: 'hidden' }}>
                            {formatLine(change.value)}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );

    const renderColumn = (title: string, changes: DiffChange[], tone: 'added' | 'removed') => (
        <Box
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'grey.50',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    px: 1.5,
                    py: 1,
                    fontWeight: 600,
                    bgcolor:
                        tone === 'removed' ? 'rgba(239, 68, 68, 0.12)' : 'rgba(34, 197, 94, 0.12)',
                    color: tone === 'removed' ? 'error.dark' : 'success.dark',
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                {title}
            </Typography>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <Box sx={{ fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 }}>
                    {changes.map((change: DiffChange, index: number) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                bgcolor:
                                    change.type === tone
                                        ? tone === 'added'
                                            ? '#e6ffec'
                                            : '#ffebe9'
                                        : 'transparent',
                            }}
                        >
                            <Box component="span" sx={{ ...lineNumberSx, width: 40, fontSize: 12 }}>
                                {index + 1}
                            </Box>
                            <Box sx={{ flex: 1, px: 1.5, py: 0.5, overflow: 'hidden' }}>
                                {formatLine(change.value)}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );

    const renderSplitView = () => {
        const leftChanges = diff.filter(
            (change) => change.type === 'removed' || change.type === 'unchanged'
        );
        const rightChanges = diff.filter(
            (change) => change.type === 'added' || change.type === 'unchanged'
        );

        return (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, height: 500 }}>
                {renderColumn(`Old Version (v${version1?.versionNumber})`, leftChanges, 'removed')}
                {renderColumn(`New Version (v${version2?.versionNumber})`, rightChanges, 'added')}
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={() => onOpenChange(false)} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                >
                    <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                        Compare Versions - {template.name}
                    </Typography>
                    <IconButton aria-label="Close" onClick={() => onOpenChange(false)} size="small">
                        <Close />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 2,
                        }}
                    >
                        <FormControl size="small" fullWidth>
                            <InputLabel>Version 1 (Old)</InputLabel>
                            <Select
                                label="Version 1 (Old)"
                                value={version1Id}
                                onChange={(event) => setVersion1Id(event.target.value)}
                            >
                                {versions.map((version) => (
                                    <MenuItem key={version.id} value={version.id}>
                                        {versionLabel(version.id)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Version 2 (New)</InputLabel>
                            <Select
                                label="Version 2 (New)"
                                value={version2Id}
                                onChange={(event) => setVersion2Id(event.target.value)}
                            >
                                {versions.map((version) => (
                                    <MenuItem key={version.id} value={version.id}>
                                        {versionLabel(version.id)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {version1 && version2 ? (
                        <Box>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                sx={{
                                    alignItems: { sm: 'center' },
                                    justifyContent: 'space-between',
                                    p: 1.5,
                                    mb: 2,
                                    bgcolor: 'grey.50',
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                }}
                            >
                                <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
                                    <Typography variant="body2">
                                        <Box
                                            component="span"
                                            sx={{
                                                color: 'success.main',
                                                fontWeight: 700,
                                                fontSize: 16,
                                                mr: 0.75,
                                            }}
                                        >
                                            +{stats.additions}
                                        </Box>
                                        additions
                                    </Typography>
                                    <Typography variant="body2">
                                        <Box
                                            component="span"
                                            sx={{
                                                color: 'error.main',
                                                fontWeight: 700,
                                                fontSize: 16,
                                                mr: 0.75,
                                            }}
                                        >
                                            -{stats.deletions}
                                        </Box>
                                        deletions
                                    </Typography>
                                    <Typography variant="body2">
                                        <Box
                                            component="span"
                                            sx={{
                                                color: 'text.secondary',
                                                fontWeight: 700,
                                                fontSize: 16,
                                                mr: 0.75,
                                            }}
                                        >
                                            {stats.unchanged}
                                        </Box>
                                        unchanged
                                    </Typography>
                                </Stack>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={viewMode}
                                    onChange={(_, next: 'unified' | 'split' | null) => {
                                        if (next) setViewMode(next);
                                    }}
                                >
                                    <ToggleButton
                                        value="unified"
                                        sx={{ textTransform: 'none', px: 2 }}
                                    >
                                        Unified
                                    </ToggleButton>
                                    <ToggleButton
                                        value="split"
                                        sx={{ textTransform: 'none', px: 2 }}
                                    >
                                        Split
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                            {viewMode === 'unified' ? renderUnifiedView() : renderSplitView()}
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 10,
                                px: 2,
                                color: 'text.secondary',
                                bgcolor: 'grey.50',
                                border: '2px dashed',
                                borderColor: 'divider',
                                borderRadius: 1,
                            }}
                        >
                            <DescriptionOutlined
                                sx={{ fontSize: 48, mb: 2, color: 'text.disabled' }}
                            />
                            <Typography sx={{ fontWeight: 500, mb: 1 }}>
                                Select two versions to compare
                            </Typography>
                            <Typography variant="body2">
                                Choose an old version and a new version from the dropdowns above
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    );
}
